import { Code2, Rocket, Cpu, Smartphone, Sparkles, LayoutDashboard } from "lucide-react";

export const SERVICES = [
  {
    slug: "custom-software",
    icon: Code2,
    badge: "Enterprise Ready",
    ctaType: "Custom Software",
    title: "Custom Software Development",
    short: "Tailor-made web & desktop engines engineered for bulletproof scale and high throughput.",
    tagline: "Software shaped to your operation — never the other way around.",
    description:
      "Off-the-shelf tools force your team to work the way the vendor decided. We design and build custom platforms around your actual workflows — from customer-facing portals to the operational backbone your whole company runs on. Every build ships with clean architecture, full test coverage, and documentation your future team will thank you for.",
    description2:
      "From our studio in Lincolnton, NC, we ship production systems for Fortune-scale enterprises and two-person startups alike. The process is the same: sharp discovery, honest scoping, weekly demoable progress, and a launch that doesn't keep anyone up at night.",
    capabilities: [
      { title: "Web Platforms & Portals", desc: "Customer portals, partner dashboards, and internal systems built on React with typed APIs." },
      { title: "API & Integration Engineering", desc: "REST, GraphQL, and event-driven integrations that connect your stack without duct tape." },
      { title: "Cloud-Native Architecture", desc: "Containerized, observable, auto-scaling systems on AWS with infrastructure as code." },
      { title: "Data Pipelines & Reporting", desc: "Reliable ETL and real-time analytics that turn raw events into decisions." },
      { title: "Security & Compliance", desc: "SSO, RBAC, audit trails, and SOC 2-friendly engineering practices baked in." },
    ],
    stack: ["React", "TypeScript", "Node.js", "Python", "PostgreSQL", "AWS", "Docker"],
    problem: [
      "Processes spread across spreadsheets, email threads, and tribal knowledge",
      "SaaS subscriptions that cover 60% of the workflow at 200% of the price",
      "Off-the-shelf tools that break the moment your business does something unique",
    ],
    solution: [
      "One system modeled on how your team actually operates",
      "You own the code, the data, and the roadmap — zero vendor lock-in",
      "Architecture designed for the next five years, documented for the next team",
    ],
    stat: { value: "3.2x", label: "average operational throughput gain after a custom build" },
    image: "https://images.unsplash.com/photo-1605379399642-870262d3d051?crop=entropy&cs=srgb&auto=format&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzb2Z0d2FyZSUyMGVuZ2luZWVyJTIwd29ya3NwYWNlJTIwZGFyayUyMG5lb258ZW58MHx8fHwxNzkwNDc0NTgyfDA&ixlib=rb-4.1.0&q=80&w=1200",
    metaTitle: "Custom Software Development Studio | volt.software",
    metaDesc: "Custom software engineered around your workflows. Web platforms, APIs, and data systems built in Lincolnton, NC for enterprises and SMBs worldwide.",
  },
  {
    slug: "saas-creation",
    icon: Rocket,
    badge: "0-to-1 Product",
    ctaType: "SaaS Product",
    title: "SaaS Creation & Launch",
    short: "End-to-end multi-tenant product architecture, billing engines, and reactive UI/UX.",
    tagline: "From idea to paying customers — without the architectural debt that kills SaaS at scale.",
    description:
      "We take SaaS products from whiteboard to revenue. That means multi-tenant architecture done right on day one, usage metering and subscription billing that survive audits, and an interface fast enough that users assume it was built by a team ten times the size.",
    description2:
      "Whether you're a founder with a napkin sketch or an enterprise spinning out a product line, we handle the full arc: discovery, UX, engineering, launch infrastructure, and the analytics loop that tells you what to build next.",
    capabilities: [
      { title: "Multi-Tenant Architecture", desc: "Row-level isolation, tenant-aware caching, and linear migration paths from day one." },
      { title: "Billing & Metering", desc: "Ledger-first usage tracking, Stripe-powered subscriptions, proration that actually works." },
      { title: "Product UI/UX", desc: "High-density, reactive interfaces with the polish users expect from market leaders." },
      { title: "Launch Infrastructure", desc: "CI/CD, observability, feature flags, and staged rollouts wired before launch day." },
      { title: "Growth Analytics", desc: "Activation, retention, and revenue instrumentation that guides the roadmap." },
    ],
    stack: ["React", "FastAPI", "PostgreSQL", "Stripe", "Redis", "AWS", "Terraform"],
    problem: [
      "Tenant isolation bolted on after launch, leaking data risk everywhere",
      "Billing logic scattered across handlers, invoices nobody can explain",
      "An MVP that can't be changed without breaking three other features",
    ],
    solution: [
      "Tenancy, entitlements, and metering as first-class architecture",
      "A typed, tested codebase that new engineers ramp on in days",
      "Launch in weeks with infrastructure that scales to your hundredth customer",
    ],
    stat: { value: "6–10 wks", label: "typical timeline from kickoff to revenue-ready launch" },
    image: "https://images.unsplash.com/photo-1579567761406-4684ee0c75b6?crop=entropy&cs=srgb&auto=format&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxlbGVjdHJpYyUyMGJsdWUlMjBhYnN0cmFjdCUyMG5lb24lMjBsaW5lcyUyMGRhcmslMjB0ZWNofGVufDB8fHx8MTc5MDQ3NDU4Mnww&ixlib=rb-4.1.0&q=80&w=1200",
    metaTitle: "SaaS Creation & Multi-Tenant Engineering | volt.software",
    metaDesc: "0-to-1 SaaS product engineering: multi-tenant architecture, billing engines, and launch infrastructure. Built by volt.software in Lincolnton, NC.",
  },
  {
    slug: "legacy-revamp",
    icon: Cpu,
    badge: "Modernization",
    ctaType: "Legacy Modernization",
    title: "Legacy System Revamp & Extensions",
    short: "Modernize brittle monoliths with zero-downtime migration and fresh frontend stacks.",
    tagline: "Your legacy system holds your business hostage. We negotiate the release.",
    description:
      "Rewrites fail. We modernize incrementally using the strangler fig pattern: carve off the most painful capability, rebuild it as a clean service, and route traffic gradually. The old system keeps running the whole time — your customers never notice a thing except that everything gets faster.",
    description2:
      "We also extend the systems you keep. New APIs, modern frontends over old backends, integrations with tools that didn't exist when your system was built. If it runs your business, we can make it run better.",
    capabilities: [
      { title: "Strangler Fig Migration", desc: "Capability-by-capability extraction behind an API gateway — zero big-bang risk." },
      { title: "Zero-Downtime Cutovers", desc: "Blue-green deploys and traffic shadowing so migration day is a non-event." },
      { title: "Frontend Replatforming", desc: "Modern React interfaces over legacy backends your team isn't ready to retire." },
      { title: "Legacy Extensions", desc: "New modules, APIs, and integrations grafted cleanly onto existing systems." },
      { title: "Data Migration", desc: "Verified, replayable migration pipelines with full audit trails." },
    ],
    stack: ["Python", "Node.js", "React", "PostgreSQL", "Kafka", "Docker", "AWS"],
    problem: [
      "Deploys measured in hours, rollbacks measured in prayer",
      "One person understands the billing module — and they're updating their résumé",
      "Every integration request gets answered with 'the system can't do that'",
    ],
    solution: [
      "The most painful 20% modernized in the first quarter — the part causing 80% of the drag",
      "Documented architecture and tests that end the single-point-of-knowledge problem",
      "A system your team can extend weekly instead of fearing quarterly",
    ],
    stat: { value: "0 min", label: "customer-facing downtime across every migration we've run" },
    image: "https://images.unsplash.com/photo-1782094673136-5198a372980c?crop=entropy&cs=srgb&auto=format&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxzZXJ2ZXIlMjByb29tJTIwcmFjayUyMGJsdWUlMjBsaWdodHN8ZW58MHx8fDE3OTA0NzQ1ODl8MA&ixlib=rb-4.1.0&q=80&w=1200",
    metaTitle: "Legacy System Revamp & Modernization | volt.software",
    metaDesc: "Zero-downtime legacy modernization and system extensions. Strangler-fig migration, frontend replatforming, data migration. volt.software, Lincolnton NC.",
  },
  {
    slug: "mobile-apps",
    icon: Smartphone,
    badge: "Cross-Platform",
    ctaType: "Mobile App",
    title: "Mobile App Development",
    short: "High-framerate iOS & Android apps for enterprise workforces and consumer startups.",
    tagline: "120fps smooth. Store-approved. Built to survive product-market fit.",
    description:
      "We build mobile apps people keep installed. That means obsessive performance discipline from sprint one — heavy work off the UI thread, aggressive image optimization, native bridges where they matter — and a release pipeline that ships installable builds on every merge.",
    description2:
      "From a founder's first consumer app to field-workforce tools for enterprise teams, one codebase covers iOS and Android without the 'cross-platform feel.'",
    capabilities: [
      { title: "Consumer Apps", desc: "App Store and Play launches with the polish that earns five-star first impressions." },
      { title: "Enterprise & Workforce", desc: "Offline-first field tools, SSO, device management, and audit logging." },
      { title: "Cross-Platform Engineering", desc: "Flutter and React Native done right — native performance without two teams." },
      { title: "Native Bridges", desc: "Camera pipelines, Bluetooth, background location — the 5% that needs native code." },
      { title: "Release Engineering", desc: "CI builds on every merge, crash symbolication, and staged store rollouts." },
    ],
    stack: ["Flutter", "React Native", "Swift", "Kotlin", "FastAPI", "Firebase"],
    problem: [
      "Two native codebases, two teams, double the budget and drift",
      "A cross-platform app that scrolls like a web page from 2011",
      "Release cycles blocked on whoever remembers how signing works",
    ],
    solution: [
      "One codebase, native feel, half the maintenance surface",
      "Performance budgets enforced in CI — jank never reaches users",
      "Automated signing and staged rollouts from the first sprint",
    ],
    stat: { value: "120fps", label: "render budget we engineer every interaction against" },
    image: "https://images.unsplash.com/photo-1773966071293-bea3d7646fd1?crop=entropy&cs=srgb&auto=format&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxkZXZlbG9wZXIlMjBjb2RpbmclMjBsYXB0b3AlMjBkYXJrJTIwc2NyZWVufGVufDB8fHx8MTc5MDQ3NDU4OXww&ixlib=rb-4.1.0&q=80&w=1200",
    metaTitle: "Enterprise & Consumer Mobile App Development | volt.software",
    metaDesc: "High-performance iOS & Android apps for startups and enterprises. Cross-platform done right by volt.software, Lincolnton NC.",
  },
  {
    slug: "ai-agents",
    icon: Sparkles,
    badge: "Autonomous AI",
    ctaType: "AI Agent / Automation",
    title: "Custom AI Agents & Workflows",
    short: "Autonomous LLM agents, RAG document systems, and operational automations that hold up in production.",
    tagline: "Agents engineered like distributed systems — not like prompts.",
    description:
      "The gap between a demo agent and a production agent is guardrails, tool design, and honest evaluation. We build custom AI agents with strict tool schemas, permission boundaries, human-approval gates, and eval harnesses that catch regressions before your users do.",
    description2:
      "Practical AI, pointed at real ROI: document intake and extraction, support triage, retrieval over your own data, and operational automations that draft decisions for humans to approve.",
    capabilities: [
      { title: "Autonomous Agents", desc: "Multi-step agents with tight tool schemas, timeouts, and deterministic fallbacks." },
      { title: "RAG Knowledge Systems", desc: "Retrieval over your documents, contracts, and wikis with cited answers." },
      { title: "Document Intelligence", desc: "Invoice, contract, and form extraction pipelines with human review queues." },
      { title: "Workflow Automation", desc: "Agents that watch, decide, and draft — your team approves with one click." },
      { title: "Evaluation Harnesses", desc: "Hundreds of real tasks replayed on every change. No vibe-checking in production." },
    ],
    stack: ["OpenAI", "Anthropic", "LangGraph", "pgvector", "Python", "FastAPI"],
    problem: [
      "A chatbot demo that collapses the moment it meets real customer data",
      "Staff copy-pasting between the LLM tab and five internal systems",
      "AI pilots with no measurement, no guardrails, and no path to production",
    ],
    solution: [
      "Agents wired into your actual systems with permission-scoped tools",
      "Human-in-the-loop gates wherever a mistake would be expensive",
      "Eval suites that prove reliability before — and after — every change",
    ],
    stat: { value: "70%+", label: "of tier-1 support volume deflected by agents we've shipped" },
    image: "https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&auto=format&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxhaSUyMGFydGlmaWNpYWwlMjBpbnRlbGxpZ2VuY2UlMjBuZXR3b3JrJTIwYnJhaW58ZW58MHx8fDE3OTA0NzQ1ODl8MA&ixlib=rb-4.1.0&q=80&w=1200",
    metaTitle: "Custom Autonomous AI Agents & Workflows | volt.software",
    metaDesc: "Production-grade AI agents, RAG systems, and workflow automation with real guardrails and evals. Built by volt.software, Lincolnton NC.",
  },
  {
    slug: "business-tools",
    icon: LayoutDashboard,
    badge: "Efficiency",
    ctaType: "Internal Business Tool",
    title: "Internal Business Tools",
    short: "High-density admin portals, real-time analytics hubs, and workflow automation for operators.",
    tagline: "The internal tools your ops team deserves — not the spreadsheet empire they've settled for.",
    description:
      "Every growing business accumulates a shadow IT of spreadsheets, exported CSVs, and Slack messages asking 'which number is right?' We build the internal tools that replace them: dense admin portals, real-time operational dashboards, approval workflows, and automation that quietly deletes hours of manual work every week.",
    description2:
      "These tools don't need to win design awards — they need to be fast, correct, and loved by the people who use them eight hours a day. That's exactly how we build them.",
    capabilities: [
      { title: "Admin Portals", desc: "Dense, keyboard-fast CRUD interfaces with granular permissions and audit logs." },
      { title: "Real-Time Dashboards", desc: "Live operational metrics streamed from your systems — no more stale exports." },
      { title: "Approval Workflows", desc: "Multi-step review chains with SLAs, escalations, and full paper trails." },
      { title: "Ops Automation", desc: "Scheduled jobs, sync pipelines, and alerting that replace manual busywork." },
      { title: "Reporting Engines", desc: "Self-serve report builders your team controls without filing a ticket." },
    ],
    stack: ["React", "TypeScript", "FastAPI", "PostgreSQL", "WebSockets", "Redis"],
    problem: [
      "Critical numbers living in a spreadsheet only one person can maintain",
      "Approvals happening in email threads nobody can audit later",
      "Ops staff spending Fridays manually reconciling systems that should talk",
    ],
    solution: [
      "One source of truth with live data from every system you run",
      "Auditable workflows with permissions that satisfy compliance",
      "Automation that hands your team their Fridays back",
    ],
    stat: { value: "12 hrs", label: "median weekly time saved per ops team after tool rollout" },
    image: "https://images.unsplash.com/photo-1623479322729-28b25c16b011?crop=entropy&cs=srgb&auto=format&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzb2Z0d2FyZSUyMGVuZ2luZWVyJTIwd29ya3NwYWNlJTIwZGFyayUyMG5lb258ZW58MHx8fHwxNzkwNDc0NTgyfDA&ixlib=rb-4.1.0&q=80&w=1200",
    metaTitle: "High-Density Business Tools & Dashboards | volt.software",
    metaDesc: "Internal admin portals, real-time dashboards, and workflow automation that replace spreadsheet chaos. volt.software, Lincolnton NC.",
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
